import Image from "next/image";
import Link from "next/link";
import { useTrans } from "@/utils/useTrans";
import ComponentErrorBoundary from "@/v2-components/pages/ComponentErrorBoundary";
import { SUPPORTED_GAMES_HREF } from "@/v2-components/pages/games/GamesSubNav";

const ICON_CLASS = "h-6 w-6 fill-blue-60";

function IconBolt() {
  return (
    <svg
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      className={ICON_CLASS}
    >
      <path d="M7 2v11h3v9l7-12h-4l4-8z" />
    </svg>
  );
}

function IconPaid() {
  return (
    <svg
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      className={ICON_CLASS}
    >
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z" />
    </svg>
  );
}

function IconGroup() {
  return (
    <svg
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      className={ICON_CLASS}
    >
      <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
    </svg>
  );
}

function IconShield() {
  return (
    <svg
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      className={ICON_CLASS}
    >
      <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z" />
    </svg>
  );
}

function IconWifi() {
  return (
    <svg
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      className={ICON_CLASS}
    >
      <path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3c-1.65-1.66-4.34-1.66-6 0zm-4-4l2 2c2.76-2.76 7.24-2.76 10 0l2-2C15.14 9.14 8.87 9.14 5 13z" />
    </svg>
  );
}

function IconSchedule() {
  return (
    <svg
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      className={ICON_CLASS}
    >
      <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z" />
    </svg>
  );
}

const ICON_MAP = {
  bolt: IconBolt,
  paid: IconPaid,
  group: IconGroup,
  shield: IconShield,
  wifi: IconWifi,
  schedule: IconSchedule,
} as const;

type IconKey = keyof typeof ICON_MAP;

const CONTENT_ITEMS: {
  categoryKey: string;
  titleKey: string;
  bodyKey: string;
  ctaKey: string;
  ctaHref?: string;
  ctaVisible: boolean;
  icon: IconKey;
  bg: string;
}[] = [
  {
    categoryKey: "live_content_1_category",
    titleKey: "live_content_1_title",
    bodyKey: "live_content_1_body",
    ctaKey: "live_content_1_cta",
    // ★개편 이전 화면(`/games/search/`)으로 가고 있었다 (#5648 후속). 개편 IA 엔 「전체
    // 게임」이 없어 PC/VR 중 하나여야 하고, 리포 선례가 「일반 Supported Games = PC」다
    // (푸터 `SUPPORTED_GAMES_PC`). 작성자 결정(2026-09-09) — CTA 문구는
    // `live_content_1_cta` = "View supported games".
    ctaHref: SUPPORTED_GAMES_HREF.pc,
    ctaVisible: true,
    icon: "bolt",
    bg: "/images/software/live/content-card-1.png",
  },
  {
    categoryKey: "live_content_2_category",
    titleKey: "live_content_2_title",
    bodyKey: "live_content_2_body",
    ctaKey: "live_content_1_cta",
    ctaVisible: false,
    icon: "paid",
    bg: "/images/software/live/content-card-2.png",
  },
  {
    categoryKey: "live_content_3_category",
    titleKey: "live_content_3_title",
    bodyKey: "live_content_3_body",
    ctaKey: "live_content_1_cta",
    ctaVisible: false,
    icon: "group",
    bg: "/images/software/live/content-card-3.png",
  },
  {
    categoryKey: "live_content_4_category",
    titleKey: "live_content_4_title",
    bodyKey: "live_content_4_body",
    ctaKey: "live_content_1_cta",
    ctaVisible: false,
    icon: "shield",
    bg: "/images/software/live/content-card-4.png",
  },
  {
    categoryKey: "live_content_5_category",
    titleKey: "live_content_5_title",
    bodyKey: "live_content_5_body",
    ctaKey: "live_content_1_cta",
    ctaVisible: false,
    icon: "wifi",
    bg: "/images/software/live/content-card-5.png",
  },
  {
    categoryKey: "live_content_6_category",
    titleKey: "live_content_6_title",
    bodyKey: "live_content_6_body",
    ctaKey: "live_content_1_cta",
    ctaVisible: false,
    icon: "schedule",
    bg: "/images/software/live/content-card-6.png",
  },
];

type ContentCardProps = {
  category: string;
  title: string;
  body: string;
  cta: string;
  ctaHref?: string;
  ctaVisible: boolean;
  icon: IconKey;
  bg: string;
  showCta?: boolean;
};

function ContentCard({
  category,
  title,
  body,
  cta,
  ctaHref,
  ctaVisible,
  icon,
  bg,
  showCta,
}: ContentCardProps) {
  const isCTAVisible = showCta ?? ctaVisible;
  const IconComponent = ICON_MAP[icon];
  const ctaClass =
    "relative text-[12px] font-600 leading-[16px] tracking-[0.1px] text-blue-60";
  return (
    <article className="relative flex h-[318px] flex-col justify-between overflow-hidden rounded-[16px] p-7 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.15)]">
      <Image
        src={bg}
        alt=""
        fill
        className="pointer-events-none object-cover"
        aria-hidden
      />

      <div className="relative flex flex-col gap-6">
        <div className="flex items-center justify-center self-start rounded-[12px] bg-blue-10 p-2">
          <IconComponent />
        </div>

        <div className="flex flex-col gap-4">
          <span className="text-[12px] font-600 leading-[16px] tracking-[0.4px] text-coolgray-70">
            {category}
          </span>
          <h3 className="whitespace-pre-line text-[22px] font-400 leading-[28px] text-coolgray-90">
            {title}
          </h3>
          <p className="whitespace-pre-line text-[14px] font-400 leading-[18px] tracking-[0.25px] text-coolgray-70">
            {body}
          </p>
        </div>
      </div>

      {ctaHref ? (
        <Link
          href={ctaHref}
          className={ctaClass}
          style={{ opacity: isCTAVisible ? 1 : 0 }}
        >
          {cta}
        </Link>
      ) : (
        <span className={ctaClass} style={{ opacity: isCTAVisible ? 1 : 0 }}>
          {cta}
        </span>
      )}
    </article>
  );
}

export default function LiveContentSection() {
  const { t } = useTrans(["software"]);
  const items = CONTENT_ITEMS.map((item) => ({
    category: t(item.categoryKey),
    title: t(item.titleKey),
    body: t(item.bodyKey),
    cta: t(item.ctaKey),
    ctaHref: item.ctaHref,
    ctaVisible: item.ctaVisible,
    icon: item.icon,
    bg: item.bg,
  }));

  return (
    <ComponentErrorBoundary>
      <section className="relative w-full overflow-x-hidden bg-white md:bg-transparent">
        <div className="absolute inset-0 hidden md:block">
          <Image
            src="/images/software/live/live-background.jpg"
            alt=""
            fill
            className="pointer-events-none object-cover"
            aria-hidden
          />
        </div>

        {/* Desktop */}
        <div className="relative hidden md:block">
          <div className="mx-auto w-full max-w-[1440px] px-4 py-[96px]">
            <h2 className="mb-8 text-center text-[32px] font-400 leading-[40px] text-coolgray-90">
              {t("live_content_heading")}
            </h2>
            <div className="grid grid-cols-3 gap-6">
              {items.map((item, i) => (
                <ContentCard key={i} {...item} />
              ))}
            </div>
          </div>
        </div>

        {/* Mobile */}
        <div className="relative block px-4 md:hidden">
          <h2 className="mb-6 text-center text-2xl font-400 leading-8 text-coolgray-90">
            {t("live_content_heading")}
          </h2>
          <div className="flex flex-col gap-6">
            {items.map((item, i) => (
              <ContentCard key={i} {...item} showCta />
            ))}
          </div>
        </div>
      </section>
    </ComponentErrorBoundary>
  );
}
